import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import { erpApi } from "../../../api/erp";
import { productsApi } from "../../../api/products";
import {
  ERP_BRAND,
  PAYMENT_METHOD_LABELS,
  entityId,
  formatMoney,
  projectProductCode,
  projectProductName,
  projectProductPrice,
  projectProductStock,
  safeNumber,
  toLocalDateTimeInput,
  uniqueRequestKey,
} from "../../../utils/erp";
import { hasProjectPermission } from "../../../utils/projectAccess";

const KIND_OPTIONS = [
  { value: "sale", label: "Venta", permission: "erp.sales.create" },
  { value: "purchase", label: "Compra", permission: "erp.purchases.create" },
  { value: "expense", label: "Gasto", permission: "erp.expenses.create" },
  { value: "internal_consumption", label: "Consumo interno", permission: "erp.internal_consumption.create" },
  { value: "stock_in", label: "Entrada de stock", permission: "erp.stock.manage" },
  { value: "stock_out", label: "Salida de stock", permission: "erp.stock.manage" },
  { value: "stock_adjustment", label: "Ajuste de stock", permission: "erp.stock.manage" },
];
const ITEM_KINDS = KIND_OPTIONS.map((option) => option.value);
const PAYMENT_OPTIONS = ["cash", "bank_transfer", "card", "digital_wallet", "check", "other"];

function emptyItem() {
  return {
    productId: "",
    variantId: "",
    name: "",
    itemType: "product",
    quantity: "1",
    adjustmentDelta: "1",
    unitPrice: "0",
    unitCost: "0",
    discountAmount: "0",
    taxAmount: "0",
    tracksStock: false,
  };
}

function productVariants(product) {
  const source = product?.variants || product?.variations || [];
  return (Array.isArray(source) ? source : []).filter((variant) => variant?.active !== false);
}

function variantIdOf(variant) {
  return entityId(variant);
}

function variantLabelOf(product, variant) {
  if (!variant) return "";
  const axes = product?.variantAttributes || product?.variantOptions || [];
  return (Array.isArray(axes) ? axes : [])
    .map((axis) => variant?.attributes?.[axis.key])
    .filter(Boolean)
    .join(" / ");
}

function variantPriceOf(product, variant) {
  const ownPrice = variant?.price;
  return ownPrice === null || ownPrice === undefined || ownPrice === ""
    ? projectProductPrice(product)
    : safeNumber(ownPrice);
}

function emptyPayment() {
  return {
    method: "cash",
    amount: "0",
    cashRegisterId: "",
    accountCode: "",
    reference: "",
  };
}

function emptyForm() {
  return {
    kind: "sale",
    occurredAt: toLocalDateTimeInput(),
    description: "",
    notes: "",
    sourceNumber: "",
    partyType: "customer",
    partyName: "",
    partyDocument: "",
    partyEmail: "",
    partyPhone: "",
    items: [emptyItem()],
    payments: [],
    idempotencyKey: uniqueRequestKey("movement-ui"),
  };
}

function normalizeExisting(movement) {
  if (!movement) return emptyForm();
  return {
    kind: movement.kind || "sale",
    occurredAt: toLocalDateTimeInput(movement.occurredAt),
    description: movement.description || "",
    notes: movement.notes || "",
    sourceNumber: movement.sourceNumber || "",
    partyType: movement.party?.type || "other",
    partyName: movement.party?.name || "",
    partyDocument: movement.party?.document || "",
    partyEmail: movement.party?.email || "",
    partyPhone: movement.party?.phone || "",
    items: (movement.items || []).length
      ? movement.items.map((item) => ({
          productId: entityId(item.productId),
          variantId: entityId(item.variantId),
          name: item.nameSnapshot || "",
          itemType: item.itemTypeSnapshot || "product",
          quantity: String(item.quantity || 1),
          adjustmentDelta: String(item.stockDelta || 1),
          unitPrice: String(item.unitPrice || 0),
          unitCost: String(item.unitCost || 0),
          discountAmount: String(item.discountAmount || 0),
          taxAmount: String(item.taxAmount || 0),
          tracksStock: Boolean(item.tracksStockSnapshot),
        }))
      : [emptyItem()],
    payments: (movement.payments || []).map((payment) => ({
      method: payment.method || "cash",
      amount: String(payment.amount || 0),
      cashRegisterId: entityId(payment.cashRegisterId),
      accountCode: payment.accountCode || "",
      reference: payment.reference || "",
    })),
    idempotencyKey: movement.idempotencyKey || uniqueRequestKey("movement-ui"),
  };
}

function lineTotal(item, kind) {
  if (["stock_in", "stock_out", "stock_adjustment"].includes(kind)) return 0;
  const quantity = Math.max(0, safeNumber(item.quantity));
  const unitValue = ["purchase", "expense", "internal_consumption"].includes(kind)
    ? safeNumber(item.unitCost || item.unitPrice)
    : safeNumber(item.unitPrice);
  return Math.max(0, quantity * unitValue - safeNumber(item.discountAmount) + safeNumber(item.taxAmount));
}

export default function MovementFormDialog({
  open,
  onClose,
  onSaved,
  projectId,
  movement = null,
  bootstrap = { registers: [], openSessions: [] },
  access = {},
}) {
  const [form, setForm] = React.useState(emptyForm);
  const [products, setProducts] = React.useState([]);
  const [loadingProducts, setLoadingProducts] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    const normalized = normalizeExisting(movement);
    if (!movement) {
      const firstAllowed = KIND_OPTIONS.find((option) => hasProjectPermission(access, option.permission));
      if (firstAllowed) normalized.kind = firstAllowed.value;
    }
    setForm(normalized);
    setError("");
  }, [access, open, movement]);

  React.useEffect(() => {
    if (!open || !projectId) return;
    let mounted = true;
    setLoadingProducts(true);
    productsApi
      .list(projectId)
      .then((data) => {
        if (mounted) setProducts(data.products || []);
      })
      .catch((err) => {
        if (mounted) setError(err.message || "No se pudieron cargar los productos");
      })
      .finally(() => {
        if (mounted) setLoadingProducts(false);
      });
    return () => {
      mounted = false;
    };
  }, [open, projectId]);

  const openRegisterIds = React.useMemo(
    () => new Set((bootstrap.openSessions || []).map((session) => entityId(session.registerId))),
    [bootstrap.openSessions],
  );

  const total = React.useMemo(
    () => form.items.reduce((sum, item) => sum + lineTotal(item, form.kind), 0),
    [form.items, form.kind],
  );
  const paid = React.useMemo(
    () => form.payments.reduce((sum, payment) => sum + safeNumber(payment.amount), 0),
    [form.payments],
  );
  const pending = Math.max(0, total - paid);
  const requiresParty = ["sale", "purchase", "expense"].includes(form.kind);
  const stockOnly = ["stock_in", "stock_out", "stock_adjustment"].includes(form.kind);
  const canAddPayments = form.kind === "sale"
    ? hasProjectPermission(access, "erp.collections.create")
    : ["purchase", "expense"].includes(form.kind)
      ? hasProjectPermission(access, "erp.payments.create")
      : false;
  const allowedKinds = React.useMemo(
    () => KIND_OPTIONS.filter((option) => hasProjectPermission(access, option.permission)),
    [access],
  );
  const productCapabilities = React.useCallback((product) => {
    const operational = product?.operational || {};
    const kind = String(product?.erpItemKind || product?.itemKind || product?.itemClass || product?.itemType || product?.tipo || "product").toLowerCase();
    const defaults = {
      product: { sellable: true, purchasable: true, stockTracked: true, expenseEnabled: false, internalConsumptionEnabled: false },
      service: { sellable: true, purchasable: false, stockTracked: false, expenseEnabled: false, internalConsumptionEnabled: false },
      supply: { sellable: false, purchasable: true, stockTracked: true, expenseEnabled: true, internalConsumptionEnabled: true },
      expense: { sellable: false, purchasable: true, stockTracked: false, expenseEnabled: true, internalConsumptionEnabled: false },
      asset: { sellable: false, purchasable: true, stockTracked: false, expenseEnabled: true, internalConsumptionEnabled: false },
      charge: { sellable: true, purchasable: true, stockTracked: false, expenseEnabled: true, internalConsumptionEnabled: false },
    }[kind] || {};
    return { ...defaults, ...operational, kind };
  }, []);
  const selectableProducts = React.useMemo(
    () => products.filter((product) => {
      const capabilities = productCapabilities(product);
      if (form.kind === "sale") return capabilities.sellable === true;
      if (form.kind === "purchase") return capabilities.purchasable === true;
      if (form.kind === "expense") return capabilities.expenseEnabled === true;
      if (form.kind === "internal_consumption") return capabilities.internalConsumptionEnabled === true;
      if (stockOnly) return capabilities.stockTracked === true;
      return true;
    }),
    [form.kind, productCapabilities, products, stockOnly],
  );

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const updateItem = (index, patch) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)),
    }));
  };

  const selectProduct = (index, productId) => {
    const product = products.find((item) => entityId(item) === productId);
    if (!product) {
      updateItem(index, { productId: "", variantId: "", name: "", tracksStock: false });
      return;
    }
    const capabilities = productCapabilities(product);
    const storedCost = product?.costing?.lastCost ?? product?.costing?.averageCost ?? product?.cost ?? 0;
    const usesCost = ["purchase", "expense", "internal_consumption"].includes(form.kind);
    const variants = productVariants(product);
    const automaticVariant = variants.length === 1 ? variants[0] : null;
    updateItem(index, {
      productId,
      variantId: automaticVariant ? variantIdOf(automaticVariant) : "",
      name: projectProductName(product),
      itemType: capabilities.kind,
      unitPrice: usesCost
        ? String(storedCost || projectProductPrice(product))
        : String(automaticVariant ? variantPriceOf(product, automaticVariant) : projectProductPrice(product)),
      unitCost: String(storedCost || 0),
      tracksStock: capabilities.stockTracked === true,
    });
  };

  const selectVariant = (index, variantId) => {
    const item = form.items[index];
    const product = products.find((candidate) => entityId(candidate) === item?.productId);
    const variant = productVariants(product).find((candidate) => variantIdOf(candidate) === variantId);
    const usesCost = ["purchase", "expense", "internal_consumption"].includes(form.kind);
    updateItem(index, {
      variantId,
      ...(!usesCost && variant ? { unitPrice: String(variantPriceOf(product, variant)) } : {}),
    });
  };

  const variantsForItem = (item) => {
    const product = products.find((candidate) => entityId(candidate) === item?.productId);
    return { product, variants: productVariants(product) };
  };

  const addItem = () => setForm((current) => ({ ...current, items: [...current.items, emptyItem()] }));
  const removeItem = (index) => setForm((current) => ({ ...current, items: current.items.filter((_, i) => i !== index) }));
  const addPayment = () => setForm((current) => ({
    ...current,
    payments: [...current.payments, { ...emptyPayment(), amount: String(Math.max(0, pending)) }],
  }));
  const updatePayment = (index, patch) => setForm((current) => ({
    ...current,
    payments: current.payments.map((payment, paymentIndex) => paymentIndex === index ? { ...payment, ...patch } : payment),
  }));
  const removePayment = (index) => setForm((current) => ({
    ...current,
    payments: current.payments.filter((_, paymentIndex) => paymentIndex !== index),
  }));

  const buildPayload = () => {
    const items = form.items.map((item) => {
      const quantity = form.kind === "stock_adjustment"
        ? Math.abs(safeNumber(item.adjustmentDelta))
        : safeNumber(item.quantity);
      const price = safeNumber(item.unitPrice);
      return {
        productId: item.productId || undefined,
        variantId: item.variantId || undefined,
        name: item.name,
        itemType: item.itemType,
        quantity,
        stockDelta: form.kind === "stock_adjustment" ? safeNumber(item.adjustmentDelta) : undefined,
        unitPrice: ["purchase", "expense", "internal_consumption"].includes(form.kind) ? safeNumber(item.unitCost || price) : price,
        unitCost: safeNumber(item.unitCost),
        discountAmount: safeNumber(item.discountAmount),
        taxAmount: safeNumber(item.taxAmount),
        tracksStock: Boolean(item.tracksStock),
      };
    });
    const payments = form.payments
      .filter((payment) => safeNumber(payment.amount) > 0)
      .map((payment) => ({
        method: payment.method,
        amount: safeNumber(payment.amount),
        cashRegisterId: payment.cashRegisterId || undefined,
        accountCode: payment.accountCode || undefined,
        reference: payment.reference,
      }));

    return {
      kind: form.kind,
      occurredAt: form.occurredAt ? new Date(form.occurredAt).toISOString() : undefined,
      documentDate: form.occurredAt ? new Date(form.occurredAt).toISOString() : undefined,
      description: form.description,
      notes: form.notes,
      sourceType: "manual_panel",
      sourceNumber: form.sourceNumber,
      idempotencyKey: form.idempotencyKey,
      party: {
        type: requiresParty ? form.partyType : "other",
        name: requiresParty ? form.partyName : "",
        document: requiresParty ? form.partyDocument : "",
        email: requiresParty ? form.partyEmail : "",
        phone: requiresParty ? form.partyPhone : "",
      },
      items,
      payments,
    };
  };

  const validate = () => {
    if (!form.description.trim()) return "Indicá una descripción para identificar el movimiento.";
    if (!ITEM_KINDS.includes(form.kind)) return "Tipo de movimiento inválido.";
    if (!form.items.length) return "Agregá al menos una línea.";

    for (let index = 0; index < form.items.length; index += 1) {
      const item = form.items[index];
      if (!item.name.trim()) return `Completá el concepto de la línea ${index + 1}.`;
      if (stockOnly && !item.productId) return `La línea ${index + 1} debe estar vinculada a un producto.`;
      const { variants } = variantsForItem(item);
      if (item.productId && variants.length && !item.variantId) {
        return `Elegí la variante de la línea ${index + 1}.`;
      }
      if (form.kind === "stock_adjustment" && safeNumber(item.adjustmentDelta) === 0) {
        return `El ajuste de la línea ${index + 1} debe ser distinto de cero.`;
      }
      if (form.kind !== "stock_adjustment" && safeNumber(item.quantity) <= 0) {
        return `La cantidad de la línea ${index + 1} debe ser mayor a cero.`;
      }
      if (["sale", "purchase", "expense"].includes(form.kind)) {
        const unitValue = ["purchase", "expense"].includes(form.kind) ? safeNumber(item.unitCost || item.unitPrice) : safeNumber(item.unitPrice);
        if (unitValue <= 0) return `El importe unitario de la línea ${index + 1} debe ser mayor a cero.`;
      }
    }
    if (paid > total && !stockOnly) return "Los pagos no pueden superar el total del movimiento.";
    for (const payment of form.payments) {
      if (payment.method === "cash" && !payment.cashRegisterId && !["owner", "admin", "superadmin"].includes(access?.role)) {
        return "Seleccioná una caja abierta para registrar el efectivo.";
      }
      if (payment.method === "cash" && payment.cashRegisterId && !openRegisterIds.has(payment.cashRegisterId)) {
        return "La caja elegida para el pago no tiene una sesión abierta.";
      }
    }
    return "";
  };

  const submit = async (confirmNow) => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      setSaving(true);
      setError("");
      const payload = buildPayload();
      const movementId = entityId(movement);
      let saved;
      if (movementId) {
        const updated = await erpApi.updateMovement(projectId, movementId, payload);
        saved = updated.movement;
        if (confirmNow) {
          const confirmed = await erpApi.confirmMovement(projectId, movementId);
          saved = confirmed.movement;
        }
      } else if (confirmNow) {
        const response = await erpApi.createConfirmedMovement(projectId, payload);
        saved = response.movement;
      } else {
        const response = await erpApi.createMovement(projectId, payload);
        saved = response.movement;
      }
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      setError(err.message || "No se pudo guardar el movimiento");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>
          {movement ? "Editar movimiento en borrador" : "Nuevo movimiento"}
        </Typography>
        <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.4 }}>
          Guardalo como borrador o confirmalo para aplicar stock, contabilidad y caja.
        </Typography>
        <IconButton onClick={onClose} disabled={saving} sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ bgcolor: "#FAFBFD" }}>
        <Stack spacing={2.5}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Box sx={{ p: 2.5, bgcolor: "#fff", borderRadius: 3, border: `1px solid ${ERP_BRAND.border}` }}>
            <Typography sx={{ fontWeight: 900, color: ERP_BRAND.dark, mb: 2 }}>Datos generales</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Tipo"
                  value={form.kind}
                  onChange={(event) => {
                    const kind = event.target.value;
                    setForm((current) => ({
                      ...current,
                      kind,
                      partyType: ["purchase", "expense"].includes(kind) ? "supplier" : "customer",
                      payments: ["sale", "purchase", "expense"].includes(kind) ? current.payments : [],
                    }));
                  }}
                >
                  {allowedKinds.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Fecha y hora"
                  type="datetime-local"
                  value={form.occurredAt}
                  onChange={(event) => updateField("occurredAt", event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Comprobante / referencia"
                  value={form.sourceNumber}
                  onChange={(event) => updateField("sourceNumber", event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  required
                  label="Descripción"
                  placeholder="Ej.: Venta de mostrador, compra de mercadería..."
                  value={form.description}
                  onChange={(event) => updateField("description", event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Notas internas"
                  value={form.notes}
                  onChange={(event) => updateField("notes", event.target.value)}
                />
              </Grid>
            </Grid>
          </Box>

          {requiresParty ? (
            <Box sx={{ p: 2.5, bgcolor: "#fff", borderRadius: 3, border: `1px solid ${ERP_BRAND.border}` }}>
              <Typography sx={{ fontWeight: 900, color: ERP_BRAND.dark, mb: 2 }}>
                {form.kind === "sale" ? "Cliente" : "Proveedor / beneficiario"}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Nombre o razón social" value={form.partyName} onChange={(event) => updateField("partyName", event.target.value)} />
                </Grid>
                <Grid item xs={12} md={2.5}>
                  <TextField fullWidth label="Documento" value={form.partyDocument} onChange={(event) => updateField("partyDocument", event.target.value)} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Email" value={form.partyEmail} onChange={(event) => updateField("partyEmail", event.target.value)} />
                </Grid>
                <Grid item xs={12} md={2.5}>
                  <TextField fullWidth label="Teléfono" value={form.partyPhone} onChange={(event) => updateField("partyPhone", event.target.value)} />
                </Grid>
              </Grid>
            </Box>
          ) : null}

          <Box sx={{ p: 2.5, bgcolor: "#fff", borderRadius: 3, border: `1px solid ${ERP_BRAND.border}` }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1.5} sx={{ mb: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 900, color: ERP_BRAND.dark }}>Ítems</Typography>
                <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                  Los productos conservan una fotografía histórica del nombre, precio y costo.
                </Typography>
              </Box>
              <Button startIcon={<AddRoundedIcon />} onClick={addItem} sx={{ textTransform: "none", fontWeight: 850 }}>
                Agregar línea
              </Button>
            </Stack>

            <Stack spacing={1.5}>
              {form.items.map((item, index) => (
                <Box key={`${index}-${item.productId}`} sx={{ p: 2, borderRadius: 2.5, bgcolor: ERP_BRAND.soft, border: `1px solid ${ERP_BRAND.border}` }}>
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} md={stockOnly ? 4 : 3}>
                      <TextField
                        select
                        fullWidth
                        label="Ítem vinculado"
                        value={item.productId}
                        onChange={(event) => selectProduct(index, event.target.value)}
                        disabled={loadingProducts}
                      >
                        {!stockOnly ? <MenuItem value="">Concepto manual</MenuItem> : null}
                        {selectableProducts.map((product) => (
                          <MenuItem key={entityId(product)} value={entityId(product)}>
                            {projectProductName(product)}{projectProductCode(product) ? ` · ${projectProductCode(product)}` : ""}{productCapabilities(product).stockTracked ? ` · stock ${projectProductStock(product)}` : ""}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    {variantsForItem(item).variants.length ? (
                      <Grid item xs={12} md={3}>
                        <TextField
                          select
                          fullWidth
                          required
                          label="Variante"
                          value={item.variantId || ""}
                          onChange={(event) => selectVariant(index, event.target.value)}
                          helperText="El stock se mueve en esta combinación"
                        >
                          <MenuItem value="">Elegí una combinación</MenuItem>
                          {variantsForItem(item).variants.map((variant) => {
                            const product = variantsForItem(item).product;
                            const label = variantLabelOf(product, variant) || variant.sku || "Variante";
                            return (
                              <MenuItem key={variantIdOf(variant)} value={variantIdOf(variant)}>
                                {label}{variant.sku ? ` · SKU ${variant.sku}` : ""} · stock {safeNumber(variant.stock)}
                              </MenuItem>
                            );
                          })}
                        </TextField>
                      </Grid>
                    ) : null}
                    <Grid item xs={12} md={stockOnly ? 3 : 2.5}>
                      <TextField fullWidth label="Concepto" value={item.name} disabled={Boolean(item.productId)} onChange={(event) => updateItem(index, { name: event.target.value })} />
                    </Grid>
                    {form.kind === "stock_adjustment" ? (
                      <Grid item xs={6} md={2}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Variación"
                          helperText="Positivo suma; negativo resta"
                          value={item.adjustmentDelta}
                          onChange={(event) => updateItem(index, { adjustmentDelta: event.target.value })}
                        />
                      </Grid>
                    ) : (
                      <Grid item xs={6} md={1.4}>
                        <TextField fullWidth type="number" label="Cantidad" value={item.quantity} onChange={(event) => updateItem(index, { quantity: event.target.value })} inputProps={{ min: 0.000001, step: "any" }} />
                      </Grid>
                    )}
                    {form.kind === "sale" ? (
                      <Grid item xs={6} md={1.8}>
                        <TextField fullWidth type="number" label="Precio unit." value={item.unitPrice} onChange={(event) => updateItem(index, { unitPrice: event.target.value })} />
                      </Grid>
                    ) : null}
                    {["purchase", "expense", "internal_consumption"].includes(form.kind) ? (
                      <Grid item xs={6} md={1.8}>
                        <TextField fullWidth type="number" label="Costo unit." value={item.unitCost} onChange={(event) => updateItem(index, { unitCost: event.target.value })} />
                      </Grid>
                    ) : null}
                    {["sale", "purchase", "expense"].includes(form.kind) ? (
                      <Grid item xs={6} md={1.4}>
                        <TextField fullWidth type="number" label="Descuento" value={item.discountAmount} onChange={(event) => updateItem(index, { discountAmount: event.target.value })} />
                      </Grid>
                    ) : null}
                    {["sale", "purchase", "expense"].includes(form.kind) ? (
                      <Grid item xs={6} md={1.4}>
                        <TextField fullWidth type="number" label="Impuesto" value={item.taxAmount} onChange={(event) => updateItem(index, { taxAmount: event.target.value })} />
                      </Grid>
                    ) : null}
                    {!stockOnly ? (
                      <Grid item xs={12} md={1.5}>
                        <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Subtotal</Typography>
                        <Typography sx={{ fontWeight: 900 }}>{formatMoney(lineTotal(item, form.kind))}</Typography>
                      </Grid>
                    ) : null}
                    <Grid item xs={12} md="auto">
                      <Stack direction="row" alignItems="center" justifyContent="flex-end">
                        {!stockOnly ? (
                          <FormControlLabel
                            control={<Checkbox checked={item.tracksStock} disabled={Boolean(item.productId)} onChange={(event) => updateItem(index, { tracksStock: event.target.checked })} />}
                            label="Stock"
                            sx={{ mr: 0 }}
                          />
                        ) : null}
                        <IconButton color="error" disabled={form.items.length === 1} onClick={() => removeItem(index)}>
                          <DeleteOutlineRoundedIcon />
                        </IconButton>
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Stack>
          </Box>

          {["sale", "purchase", "expense"].includes(form.kind) ? (
            <Box sx={{ p: 2.5, bgcolor: "#fff", borderRadius: 3, border: `1px solid ${ERP_BRAND.border}` }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1.5}>
                <Box>
                  <Typography sx={{ fontWeight: 900, color: ERP_BRAND.dark }}>Pagos</Typography>
                  <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                    El importe sin pagar queda automáticamente en cuenta corriente.
                  </Typography>
                </Box>
                <Button startIcon={<AddRoundedIcon />} onClick={addPayment} disabled={!canAddPayments} sx={{ textTransform: "none", fontWeight: 850 }}>
                  {canAddPayments ? "Agregar pago" : "Sin permiso para cobrar/pagar"}
                </Button>
              </Stack>

              <Stack spacing={1.5} sx={{ mt: 2 }}>
                {form.payments.map((payment, index) => (
                  <Box key={index} sx={{ p: 2, borderRadius: 2.5, bgcolor: ERP_BRAND.soft }}>
                    <Grid container spacing={1.5} alignItems="center">
                      <Grid item xs={12} md={2.8}>
                        <TextField select fullWidth label="Método" value={payment.method} onChange={(event) => updatePayment(index, { method: event.target.value, cashRegisterId: "" })}>
                          {PAYMENT_OPTIONS.map((method) => <MenuItem key={method} value={method}>{PAYMENT_METHOD_LABELS[method]}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <TextField fullWidth type="number" label="Importe" value={payment.amount} onChange={(event) => updatePayment(index, { amount: event.target.value })} />
                      </Grid>
                      {payment.method === "cash" ? (
                        <Grid item xs={12} md={3.5}>
                          <TextField select fullWidth label="Caja física" value={payment.cashRegisterId} onChange={(event) => updatePayment(index, { cashRegisterId: event.target.value })} helperText="Solo las cajas abiertas impactan el arqueo">
                            {["owner", "admin", "superadmin"].includes(access?.role) ? (
                              <MenuItem value="">Caja contable general</MenuItem>
                            ) : null}
                            {(bootstrap.registers || []).map((register) => (
                              <MenuItem key={entityId(register)} value={entityId(register)} disabled={!openRegisterIds.has(entityId(register))}>
                                {register.name} {openRegisterIds.has(entityId(register)) ? "· abierta" : "· cerrada"}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      ) : (
                        <Grid item xs={12} md={3.5}>
                          <TextField fullWidth label="Cuenta contable (opcional)" value={payment.accountCode} onChange={(event) => updatePayment(index, { accountCode: event.target.value.toUpperCase() })} placeholder="BANK" />
                        </Grid>
                      )}
                      <Grid item xs={10} md={2.5}>
                        <TextField fullWidth label="Referencia" value={payment.reference} onChange={(event) => updatePayment(index, { reference: event.target.value })} />
                      </Grid>
                      <Grid item xs={2} md={1.2}>
                        <IconButton color="error" onClick={() => removePayment(index)}><DeleteOutlineRoundedIcon /></IconButton>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                {!canAddPayments ? <Alert severity="info">Podés registrar el comprobante, pero el cobro o pago debe hacerlo un miembro autorizado.</Alert> : !form.payments.length ? <Alert severity="info">Sin pagos cargados: el total quedará pendiente en cuenta corriente.</Alert> : null}
              </Stack>
            </Box>
          ) : null}

          <Box sx={{ p: 2.5, bgcolor: ERP_BRAND.dark, color: "#fff", borderRadius: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.62)" }}>TOTAL</Typography>
                <Typography variant="h5" sx={{ fontWeight: 950 }}>{formatMoney(total)}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.62)" }}>PAGADO</Typography>
                <Typography variant="h5" sx={{ fontWeight: 950 }}>{formatMoney(paid)}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.62)" }}>PENDIENTE</Typography>
                <Typography variant="h5" sx={{ fontWeight: 950 }}>{formatMoney(pending)}</Typography>
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2, gap: 1, flexWrap: "wrap" }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", fontWeight: 850 }}>Cancelar</Button>
        <Button variant="outlined" startIcon={<SaveRoundedIcon />} disabled={saving} onClick={() => submit(false)} sx={{ textTransform: "none", fontWeight: 850 }}>
          Guardar borrador
        </Button>
        <Button variant="contained" startIcon={<TaskAltRoundedIcon />} disabled={saving} onClick={() => submit(true)} sx={{ textTransform: "none", fontWeight: 850 }}>
          {saving ? "Procesando..." : "Guardar y confirmar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
